/**
 * RegistresCustomAdmin (V35)
 * UI Super Admin pour créer et gérer des registres custom (nocode).
 * Chaque registre a un nom, une description, une icône, une catégorie GED,
 * et un schéma de champs configurables.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2, Edit3, ChevronRight, BookOpen, FolderOpen, Loader2, Search, ArrowLeft, X, RefreshCw, Download } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from './ui/select';
import { Textarea } from './ui/textarea';
import { getToken } from '../services/tokenService';
import { RegistreFormDialog, EntryFormDialog } from './RegistresCustomDialogs';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api/registres-custom`;


const authHeaders = () => ({ Authorization: `Bearer ${getToken()}` });

// ---------- Composant principal ----------
export default function RegistresCustomAdmin() {
  const [registres, setRegistres] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRegistre, setSelectedRegistre] = useState(null);

  const [entries, setEntries] = useState([]);
  const [entriesLoading, setEntriesLoading] = useState(false);
  const [entrySearch, setEntrySearch] = useState('');

  const [registreFormOpen, setRegistreFormOpen] = useState(false);
  const [registreEditTarget, setRegistreEditTarget] = useState(null);

  const [entryFormOpen, setEntryFormOpen] = useState(false);
  const [entryEditTarget, setEntryEditTarget] = useState(null);

  const fetchRegistres = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin`, { headers: authHeaders() });
      setRegistres(res.data.items || []);
    } catch {
      toast.error('Erreur de chargement des registres');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchEntries = useCallback(async () => {
    if (!selectedRegistre) return;
    setEntriesLoading(true);
    try {
      const params = entrySearch ? { search: entrySearch } : {};
      const res = await axios.get(`${API}/admin/${selectedRegistre.id}/entries`, {
        headers: authHeaders(), params,
      });
      setEntries(res.data.items || []);
      // Re-sync selectedRegistre with fresh field schema
      if (res.data.registre) setSelectedRegistre(res.data.registre);
    } catch {
      toast.error('Erreur de chargement des entrées');
    } finally {
      setEntriesLoading(false);
    }
  }, [selectedRegistre, entrySearch]);

  useEffect(() => { fetchRegistres(); }, [fetchRegistres]);
  useEffect(() => { if (selectedRegistre) fetchEntries(); }, [selectedRegistre, fetchEntries]);

  const handleDeleteRegistre = async (r) => {
    if (!window.confirm(`Supprimer le registre "${r.nom}" et toutes ses entrées ?`)) return;
    try {
      await axios.delete(`${API}/admin/${r.id}`, { headers: authHeaders() });
      toast.success('Registre supprimé');
      if (selectedRegistre?.id === r.id) setSelectedRegistre(null);
      fetchRegistres();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur');
    }
  };

  const handleDeleteEntry = async (e) => {
    if (!window.confirm('Supprimer cette entrée ?')) return;
    try {
      await axios.delete(`${API}/admin/entries/${e.id}`, { headers: authHeaders() });
      toast.success('Entrée supprimée');
      fetchEntries();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur');
    }
  };

  const handleExportExcel = async () => {
    if (!selectedRegistre) return;
    try {
      const res = await axios.get(
        `${API}/admin/${selectedRegistre.id}/export-excel`,
        { headers: authHeaders(), responseType: 'blob' }
      );
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const today = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      a.download = `export_${selectedRegistre.slug || 'registre'}_${today}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export Excel téléchargé');
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur d'export");
    }
  };

  // === Vue détaillée d'un registre ===
  if (selectedRegistre) {
    return (
      <div className="space-y-4" data-testid="registre-custom-detail">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <Button variant="ghost" size="sm" onClick={() => setSelectedRegistre(null)}>
                  <ArrowLeft className="w-4 h-4 mr-1" /> Retour
                </Button>
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <BookOpen className="w-5 h-5 text-blue-600" />
                    {selectedRegistre.nom}
                  </CardTitle>
                  {selectedRegistre.description && (
                    <CardDescription>{selectedRegistre.description}</CardDescription>
                  )}
                </div>
              </div>
              <Button onClick={() => { setEntryEditTarget(null); setEntryFormOpen(true); }} className="bg-blue-600 hover:bg-blue-700" data-testid="open-create-custom-entry-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nouvelle entrée
              </Button>
            </div>
            <div className="flex justify-end mt-2">
              <Button variant="outline" size="sm" onClick={handleExportExcel} data-testid="export-excel-btn">
                <Download className="w-4 h-4 mr-2" />
                Exporter en Excel
              </Button>
            </div>
            {selectedRegistre.ged_categorie && (
              <div className="mt-2 flex items-center gap-2 text-sm text-gray-600">
                <FolderOpen className="w-4 h-4 text-blue-500" />
                Catégorie GED :{' '}
                <Badge variant="outline" className="bg-blue-50 text-blue-700">{selectedRegistre.ged_categorie}</Badge>
              </div>
            )}
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2 mb-3">
              <div className="relative flex-1">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  placeholder="Rechercher..."
                  value={entrySearch}
                  onChange={(e) => setEntrySearch(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Button variant="ghost" size="sm" onClick={fetchEntries}>
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
            <div className="border rounded-lg overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-gray-700">
                  <tr>
                    {(selectedRegistre.fields || []).map((f) => (
                      <th key={f.key} className="px-3 py-2 text-left">{f.label}</th>
                    ))}
                    <th className="px-3 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {entriesLoading && (
                    <tr><td colSpan={selectedRegistre.fields.length + 1} className="px-3 py-8 text-center">
                      <Loader2 className="w-5 h-5 mx-auto animate-spin" />
                    </td></tr>
                  )}
                  {!entriesLoading && entries.length === 0 && (
                    <tr><td colSpan={selectedRegistre.fields.length + 1} className="px-3 py-8 text-center text-gray-500">
                      Aucune entrée. Cliquez sur « Nouvelle entrée » pour commencer.
                    </td></tr>
                  )}
                  {!entriesLoading && entries.map((e) => (
                    <tr key={e.id} className="border-t hover:bg-gray-50">
                      {(selectedRegistre.fields || []).map((f) => (
                        <td key={f.key} className="px-3 py-2">
                          {f.type === 'boolean'
                            ? (e.fields?.[f.key] ? '✓' : '—')
                            : (e.fields?.[f.key] || '—')}
                        </td>
                      ))}
                      <td className="px-3 py-2 text-right">
                        <Button size="sm" variant="ghost" onClick={() => { setEntryEditTarget(e); setEntryFormOpen(true); }}>
                          <Edit3 className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50" onClick={() => handleDeleteEntry(e)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
        <EntryFormDialog
          open={entryFormOpen}
          onOpenChange={setEntryFormOpen}
          registre={selectedRegistre}
          initial={entryEditTarget}
          onSaved={fetchEntries}
        />
      </div>
    );
  }

  // === Vue liste des registres ===
  return (
    <div className="space-y-4" data-testid="registres-custom-admin">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-blue-600" />
                Registres Custom (nocode)
              </CardTitle>
              <CardDescription>
                Créez des registres métier libres (sinistres, conventions, bénévoles…) avec leurs propres champs
                et connectez-les à la GED par catégorie.
              </CardDescription>
            </div>
            <Button onClick={() => { setRegistreEditTarget(null); setRegistreFormOpen(true); }} className="bg-blue-600 hover:bg-blue-700" data-testid="open-create-registre-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nouveau registre
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading && (
            <div className="text-center py-8">
              <Loader2 className="w-6 h-6 mx-auto animate-spin text-gray-400" />
            </div>
          )}
          {!loading && registres.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <BookOpen className="w-12 h-12 mx-auto text-gray-300 mb-3" />
              <p>Aucun registre custom pour le moment.</p>
              <p className="text-sm mt-1">Cliquez sur « Nouveau registre » pour commencer.</p>
            </div>
          )}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {registres.map((r) => (
              <div
                key={r.id}
                className="border rounded-xl p-4 hover:shadow-md transition-all cursor-pointer bg-white relative group"
                data-testid={`registre-card-${r.id}`}
              >
                <div onClick={() => setSelectedRegistre(r)} className="cursor-pointer">
                  <div className="flex items-start justify-between mb-2">
                    <BookOpen className="w-6 h-6 text-blue-600" />
                    <Badge variant="outline" className="text-xs">
                      {r.entries_count || 0} entrée{(r.entries_count || 0) > 1 ? 's' : ''}
                    </Badge>
                  </div>
                  <h4 className="font-bold text-gray-900 mt-1">{r.nom}</h4>
                  {r.description && (
                    <p className="text-xs text-gray-600 mt-1 line-clamp-2">{r.description}</p>
                  )}
                  {r.ged_categorie && (
                    <div className="flex items-center gap-1 mt-2 text-xs text-blue-600">
                      <FolderOpen className="w-3.5 h-3.5" />
                      GED : <span className="font-mono">{r.ged_categorie}</span>
                    </div>
                  )}
                  <div className="text-xs text-gray-400 mt-2">
                    {(r.fields || []).length} champ{(r.fields || []).length > 1 ? 's' : ''}
                  </div>
                  <ChevronRight className="absolute top-1/2 right-3 -translate-y-1/2 w-4 h-4 text-gray-300 group-hover:text-blue-500" />
                </div>
                <div className="flex items-center justify-end gap-1 mt-3 pt-3 border-t border-gray-100">
                  <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setRegistreEditTarget(r); setRegistreFormOpen(true); }}>
                    <Edit3 className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50" onClick={(e) => { e.stopPropagation(); handleDeleteRegistre(r); }}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
      <RegistreFormDialog
        open={registreFormOpen}
        onOpenChange={setRegistreFormOpen}
        initial={registreEditTarget}
        onSaved={fetchRegistres}
      />
    </div>
  );
}
